/**
 * King Living Tag Theme
 *
 * Global tag styling system supporting:
 * - 2 sizes: large (big) and small
 * - Multiple variants: sale, new, out-of-stock, in-stock, low-stock, order-confirmed, delivered, most-popular
 * - Consistent typography and spacing
 *
 * Order status lozenges (Figma ACCHIST product tags):
 * - order-confirmed: brown bg / white text — confirmed, payment due, delivery date, out for delivery
 * - delivered: white bg / brown text
 * - order-cancelled / order-refunded: error bg / error text
 *
 * @override Extends Chakra UI Tag theme
 */

const orderStatusTagTypography = {
  container: {
    fontWeight: '400',
  },
  label: {
    fontWeight: '400',
  },
};

const Tag = {
  parts: ['container', 'label'],
  baseStyle: {
    container: {
      display: 'flex',
      alignItems: 'center',
      gap: 2, // 8px
      borderRadius: 'base', // 4px
      fontSize: 'xs', // 12px
      fontFamily: 'body',
      fontWeight: '600', // SemiBold
      lineHeight: '120%', // 1.2
      fontStyle: 'normal',
      whiteSpace: 'nowrap',
    },
    label: {
      fontSize: 'xs', // 12px
      fontFamily: 'body',
      fontWeight: '600', // SemiBold
      lineHeight: '120%', // 1.2
      fontStyle: 'normal',
    },
  },
  sizes: {
    large: {
      container: {
        px: 2, // 8px
        py: 1, // 4px
      },
    },
    small: {
      container: {
        px: 1.5, // 6px
        py: 0.5, // 2px
      },
    },
  },
  variants: {
    'sale': {
      container: {
        bg: '#cd2a2d', // Sale red from Figma
        color: 'white',
      },
    },
    'new': {
      container: {
        bg: 'brandColors.greyscale90', // rgba(27, 26, 24, 0.9)
        color: 'white',
      },
    },
    'out-of-stock': {
      container: {
        bg: 'brandColors.error', // #9d2a1e
        color: 'white',
      },
    },
    'in-stock': {
      container: {
        bg: 'brandColors.success', // #2d7831
        color: 'white',
      },
    },
    'low-stock': {
      container: {
        bg: 'brandColors.warning', // #d97706
        color: 'white',
      },
    },
    'order-confirmed': {
      container: {
        bg: 'brandColors.primaryDarkLinen', // #8c683b
        color: 'white',
        ...orderStatusTagTypography.container,
      },
      label: orderStatusTagTypography.label,
    },
    'order-cancelled': {
      container: {
        bg: 'brandColors.errorBG', // #fef2f2
        color: 'brandColors.error', // #9d2a1e
        ...orderStatusTagTypography.container,
      },
      label: orderStatusTagTypography.label,
    },
    'order-refunded': {
      container: {
        bg: 'brandColors.errorBG', // #fef2f2
        color: 'brandColors.error', // #9d2a1e
        ...orderStatusTagTypography.container,
      },
      label: orderStatusTagTypography.label,
    },
    'delivered': {
      container: {
        bg: 'white',
        color: 'brandColors.primaryDarkLinen', // #8c683b
        ...orderStatusTagTypography.container,
      },
      label: orderStatusTagTypography.label,
    },
    'most-popular': {
      container: {
        bg: 'brandColors.greyscale90', // rgba(27, 26, 24, 0.9)
        color: 'white',
      },
    },
  },
  defaultProps: {
    size: 'large',
    variant: 'new',
  },
};

export default Tag;
